import * as S from "./styled";
import cn from "classnames";
import T from "@marginly/ui/components/typography";
import TextField from "@marginly/ui/components/input/text-field";
import Button from "@marginly/ui/components/button";
import React, { ChangeEvent, useCallback, useEffect, useState } from "react";
import { MemberInfoResponse, addMember } from "util/api-requests";
import { trackRegistrationSubmitClick, trackSignInClick } from "util/tracking";
import { useWeb3Modal } from "@web3modal/wagmi/react";
import { useAccount } from "wagmi";
import { useRx } from "contexts/rx";
import { useObsState } from "components/hooks/rxjs";
import { createBoundBehaviorSubject } from "util/rx/bound-subject";
import AlertIcon from "public/images/icons/alert";

const emailRegex =
  // eslint-disable-next-line no-useless-escape
  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{1,}))$/;

const validateEmail = (email: string) => {
  return emailRegex.test(email.toLowerCase());
};

interface Props {
  contestId: number;
  onSubmit: () => void;
  refCode: string | null | undefined;
}

export function RegisterForm({ contestId, onSubmit, refCode }: Props) {
  const { open } = useWeb3Modal();
  const [isRegisterClicked, setIsRegisterClicked] = React.useState(false);
  const { address } = useAccount();

  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState(false);
  const { notification$ } = useRx();
  const { subject } = createBoundBehaviorSubject("", "referral");

  const referral = useObsState(subject);

  const addMemberRequest = useCallback(
    async (address: string) => {
      const postParams: MemberInfoResponse = {
        email,
        userAddress: address,
        contestId: contestId,
      };
      if (referral && referral.length > 0) {
        postParams.refCode = referral;
      }
      await addMember(postParams);
      onSubmit();
      trackRegistrationSubmitClick();
    },
    [email, contestId, referral, onSubmit],
  );

  const handleRegistration: React.FormEventHandler<HTMLFormElement> = async (
    e,
  ) => {
    e.preventDefault();
    if (email && !validateEmail(email)) {
      notification$.next({
        message: "Enter correct email address",
      });
      setEmailError(true);
      return;
    }
    setIsRegisterClicked(true);
    if (!address) {
      trackSignInClick();
      open();
      return;
    }
  };

  const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    setEmailError(false);
  };

  useEffect(() => {
    if (isRegisterClicked && address && refCode === null) {
      addMemberRequest(address);
    }
  }, [addMemberRequest, address, isRegisterClicked, refCode]);

  return (
    <S.Form onSubmit={handleRegistration}>
      <TextField
        placeholder="Your email (optional)"
        value={email}
        className={cn({ error: emailError }, "text-field")}
        onChange={handleEmailChange}
      >
        {emailError ? <AlertIcon /> : <div></div>}
      </TextField>
      <Button lg>
        <T action>Register</T>
      </Button>
    </S.Form>
  );
}
